import { useState } from "react";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useCaptcha } from "@/context/CaptchaContext";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEGAL_DISCLAIMER = "Este servicio es de acompañamiento y escucha. No es un servicio médico y no sustituye atención profesional.";

export default function Participate() {
  const { executeRecaptcha, enabled: captchaEnabled } = useCaptcha();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: ""
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Get captcha token if enabled
      let captchaToken = null;
      if (captchaEnabled && executeRecaptcha) {
        captchaToken = await executeRecaptcha("participate");
      }
      
      await axios.post(`${API}/participate`, {
        ...formData,
        captcha_token: captchaToken
      });
      setSubmitted(true);
      toast.success("¡Solicitud enviada!");
    } catch (error) {
      console.error("Error submitting:", error);
      toast.error(error.response?.data?.detail || "Error al enviar la solicitud");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center py-12 px-4">
          <div className="text-center max-w-md">
            <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-primary" />
            </div>
            <h1 className="text-3xl font-serif font-bold text-foreground">¡Gracias por tu interés!</h1>
            <p className="mt-4 text-stone-600">
              Hemos recibido tu solicitud. Nos pondremos en contacto contigo pronto.
            </p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container-main py-16">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-serif font-bold text-foreground">Me gustaría participar</h1>
            <p className="mt-4 text-lg text-stone-600">
              ¿Te gustaría ser parte de Apoyum como listener? Comparte tus datos y nos pondremos en contacto.
            </p>
          </div>

          <div className="bg-white rounded-3xl shadow-xl border border-stone-100 p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="name">Nombre completo</Label>
                <Input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Tu nombre"
                  required
                  className="mt-2"
                  data-testid="participate-name"
                />
              </div>

              <div>
                <Label htmlFor="email">Correo electrónico</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="tu@email.com"
                  required
                  className="mt-2"
                  data-testid="participate-email"
                />
              </div>

              <div>
                <Label htmlFor="message">Cuéntanos sobre ti</Label>
                <Textarea
                  id="message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="¿Por qué te gustaría ser listener? ¿Cuál es tu experiencia?"
                  required
                  rows={5}
                  className="mt-2"
                  data-testid="participate-message"
                />
              </div>

              <Button
                type="submit"
                className="w-full btn-primary text-lg py-6"
                disabled={loading}
                data-testid="participate-submit"
              >
                {loading ? "Enviando..." : "Enviar solicitud"}
              </Button>
            </form>

            <p className="mt-6 legal-disclaimer text-center">
              {LEGAL_DISCLAIMER}
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
}
